import { useState, useEffect } from "react";

interface MarketStatus {
  isOpen: boolean;
  statusText: string;
  nextEvent: string;
  countdown: string;
}

// NYSE holidays for 2025-2027 (pre-computed for performance)
const NYSE_HOLIDAYS: Record<number, string[]> = {
  2025: [
    "2025-01-01", // New Year's Day
    "2025-01-20", // MLK Day
    "2025-02-17", // Presidents Day
    "2025-04-18", // Good Friday
    "2025-05-26", // Memorial Day
    "2025-06-19", // Juneteenth
    "2025-07-04", // Independence Day
    "2025-09-01", // Labor Day
    "2025-11-27", // Thanksgiving
    "2025-12-25", // Christmas
  ],
  2026: [
    "2026-01-01", // New Year's Day
    "2026-01-19", // MLK Day
    "2026-02-16", // Presidents Day
    "2026-04-03", // Good Friday
    "2026-05-25", // Memorial Day
    "2026-06-19", // Juneteenth
    "2026-07-03", // Independence Day (observed)
    "2026-09-07", // Labor Day
    "2026-11-26", // Thanksgiving
    "2026-12-25", // Christmas
  ],
  2027: [
    "2027-01-01", // New Year's Day
    "2027-01-18", // MLK Day
    "2027-02-15", // Presidents Day
    "2027-03-26", // Good Friday
    "2027-05-31", // Memorial Day
    "2027-06-18", // Juneteenth (observed)
    "2027-07-05", // Independence Day (observed)
    "2027-09-06", // Labor Day
    "2027-11-25", // Thanksgiving
    "2027-12-24", // Christmas (observed)
  ],
};

function isNYSEHoliday(date: Date): boolean {
  const year = date.getFullYear();
  const dateStr = date.toISOString().split("T")[0];
  return NYSE_HOLIDAYS[year]?.includes(dateStr) ?? false;
}

function getMarketStatus(): MarketStatus {
  const now = new Date();
  
  // Convert to Eastern Time
  const etOptions: Intl.DateTimeFormatOptions = {
    timeZone: "America/New_York",
    hour: "numeric",
    minute: "numeric",
    hour12: false,
  };
  const etTimeStr = now.toLocaleString("en-US", etOptions);
  const [hours, minutes] = etTimeStr.split(":").map(Number);
  const currentMinutes = hours * 60 + minutes;
  
  // Get ET date for holiday check
  const etDateStr = now.toLocaleDateString("en-CA", { timeZone: "America/New_York" });
  const etDate = new Date(etDateStr + "T12:00:00");
  const dayOfWeek = etDate.getDay(); // 0=Sun, 6=Sat
  
  // Market hours: 9:30 AM - 4:00 PM ET
  const marketOpen = 9 * 60 + 30; // 9:30 AM = 570 minutes
  const marketClose = 16 * 60; // 4:00 PM = 960 minutes
  
  // Check if weekend
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    const daysUntilMonday = dayOfWeek === 0 ? 1 : 2;
    return {
      isOpen: false,
      statusText: "Weekend",
      nextEvent: "Opens Monday",
      countdown: `${daysUntilMonday}d`,
    };
  }
  
  // Check if holiday
  if (isNYSEHoliday(etDate)) {
    return {
      isOpen: false,
      statusText: "Holiday",
      nextEvent: "Market Holiday",
      countdown: "—",
    };
  }
  
  // Check market hours
  if (currentMinutes < marketOpen) {
    // Pre-market
    const minsUntilOpen = marketOpen - currentMinutes;
    const hrs = Math.floor(minsUntilOpen / 60);
    const mins = minsUntilOpen % 60;
    return {
      isOpen: false,
      statusText: "Pre-Market",
      nextEvent: "Opens at 9:30 AM ET",
      countdown: hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`,
    };
  }
  
  if (currentMinutes >= marketClose) {
    // After hours
    return {
      isOpen: false,
      statusText: "After Hours",
      nextEvent: "Opens tomorrow 9:30 AM ET",
      countdown: "—",
    };
  }
  
  // Market is open
  const minsUntilClose = marketClose - currentMinutes;
  const hrs = Math.floor(minsUntilClose / 60);
  const mins = minsUntilClose % 60;
  return {
    isOpen: true,
    statusText: "Market Open",
    nextEvent: "Closes at 4:00 PM ET",
    countdown: hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`,
  };
}

export function MarketStatusBanner() {
  const [status, setStatus] = useState<MarketStatus>(getMarketStatus);
  
  useEffect(() => {
    // Update every minute
    const interval = setInterval(() => {
      setStatus(getMarketStatus());
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);
  
  return (
    <div
      className={`px-3 py-1.5 rounded-lg flex items-center gap-2 text-xs font-medium transition-colors ${
        status.isOpen
          ? "bg-green-500/10 border border-green-500/20 text-green-400"
          : "bg-surface-800/50 border border-surface-700/50 text-surface-400"
      }`}
    >
      {/* Pulsing dot for open market */}
      <span
        className={`w-2 h-2 rounded-full ${
          status.isOpen ? "bg-green-400 animate-pulse" : "bg-surface-500"
        }`}
      />
      
      <span className={status.isOpen ? "text-green-300" : "text-surface-300"}>
        {status.statusText}
      </span>
      
      <span className="text-surface-500">•</span>
      
      <span className="text-surface-400">
        {status.nextEvent}
      </span>
      
      {status.countdown !== "—" && (
        <>
          <span className="text-surface-500">•</span>
          <span className={status.isOpen ? "text-green-400" : "text-surface-300"}>
            {status.countdown}
          </span>
        </>
      )}
    </div>
  );
}
